<template>
  <div v-html="html"></div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import MarkdownIt from 'markdown-it'
import footnote from 'markdown-it-footnote'
import deflist from 'markdown-it-deflist'
import abbreviation from 'markdown-it-abbr'
import mark from 'markdown-it-mark'

const props = withDefaults(defineProps<{ body: string }>(), {
  body: '',
})

const md = MarkdownIt().use(footnote).use(deflist).use(abbreviation).use(mark)

md.set({
  html: true,
  xhtmlOut: true,
  breaks: true,
  linkify: true,
  typographer: true,
  langPrefix: 'language-',
  quotes: '“”‘’',
})

md.renderer.rules.table_open = () => `<table class="AppMarkdown_table">\n`

// NOTE: https://github.com/markdown-it/markdown-it/blob/master/docs/architecture.md
type RenderRule = Exclude<typeof md.renderer.rules.link_open, undefined>
const defaultLinkRenderer: RenderRule =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, _, self) => self.renderToken(tokens, idx, options))

const anchorAttributes = {
  class: 'AppMarkdown_anchor',
  target: '_blank',
  rel: 'noopener noreferrer nofollow',
}

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  Object.keys(anchorAttributes).forEach(attribute => {
    const attrKey = attribute as keyof typeof anchorAttributes
    const attrIndex = tokens[idx].attrIndex(attrKey)

    const value = anchorAttributes[attrKey]
    const token = tokens[idx]

    if (attrIndex < 0) {
      token.attrPush([attribute, value]) // add new attribute
      return
    }

    if (token.attrs) {
      token.attrs[attrIndex][1] = value
    }
  })
  return defaultLinkRenderer(tokens, idx, options, env, self)
}

const html = computed(() => md.render(props.body))
</script>
